// ═══════════════════════════════════════════════════════════════════
//  TOQUM · Quality Management System — App Shell
//  Independent product. Shell pattern reused from Nirvikalp App.js
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import { ErrorBoundary, API_URL, T, uid, today, num, hasRight, apiPost, Toast } from './Shared';
import AdminModule, { QualityVisionCard } from './Admin';
import TeamModule       from './Team';
import SupplierModule   from './Supplier';
import StyleModule      from './StyleModule';
import QAModule         from './QAModule';
import QualityDashboard from './QualityDashboard';
import SampleEvaluation  from './SampleEvaluation';
import CustomerFeedback  from './CustomerFeedback';
import Training         from './Training';
import Andon            from './Andon';
import SupplierPortalHome from './SupplierPortal';
import ProjectModule from './ProjectModule';
import CommercialDecisions from './CommercialDecision';
import LandingPage from './LandingPage';
import './index.css';
const MODULES = {
  HOME:      { label:'HOME',      icon:'🏠' },
  DASHBOARD: { label:'DASHBOARD', icon:'📊' },
  ADMIN:     { label:'ADMIN',     icon:'⚙️' },
  TEAM:      { label:'TEAM',      icon:'👥' },
  SUPPLIER:  { label:'SUPPLIER',  icon:'🏭' },
  STYLE:     { label:'STYLE',     icon:'👔' },
  SAMPLE:    { label:'SAMPLE',    icon:'🧵' },
  QA:        { label:'QA',        icon:'✅' },
  CD:        { label:'COMM. DEC.', icon:'🔴' },
  PROJECTS:  { label:'PROJECTS',  icon:'📋' },
  FEEDBACK:  { label:'RETAIL VOICE', icon:'📣' },
  TRAINING:  { label:'TRAINING',     icon:'🎓' },
};

// ─── EMPTY DB SHAPE (all Toqum data categories) ─────────────────────
const EMPTY_DB = {
  // Admin
  users: [{ id:1, username:'admin', password:'Toqum@123', name:'Super Admin', email:'admin@toqum.com',
            role:'Super Admin', status:'Active', emailVerified:true, rights:{} }],
  pendingUsers: [],
  brands: [], countries: [], regions: [], cities: [],
  supplierCategories: ['Garment','Fabric','Trims & Accessory','Washing','Embroidery','Printing'],
  qualityProcess: [],           // per-brand step-wise process + timeline offset from PO date
  brandAllocations: [],         // supplier → brand
  qaAllocations: [],            // supplier → QA member (done by manager)
  defectCodes: [],
  // Inspection formats are built in (see FormatSchemas.js) — no ISO code master needed
  styleAllocations: [],        // style → supplier → QA when not set by merchandising
  sampleEvaluations: [],       // stage-wise sample evaluation reports
  followUpChecklists: [],      // points carried from sample + risk assessment
  customerComplaints: [],      // customer feedback / claims
  emailTemplates: [],          // complaint & CAP mail templates
  trainingCourses: [],         // course library
  trainingEnrolments: [],      // who is on which course
  trainingAttempts: [],        // assessment attempts
  trainingInvitations: [],     // external candidate invitations
  certifications: [],          // course certificates + CQB
  cqbRules: [],                // which courses entitle a supplier QA to CQB, per brand/level
  qualityRecords: [],          // completed J-01..J-05 records
  supplierSubmissions: [],     // forms filled by suppliers via link, awaiting QA review
  // Team
  teamMembers: [], visitPlans: [], reminders: [], kpiPrefs: [], actionPlans: [],
  // Supplier
  suppliers: [], audits: [], caps: [], supplierRatings: [],
  warningLetters: [], blacklist: [], claims: [],
  // Style / PO
  styles: [], pos: [], qualityPlans: [], productionLogs: [],
  poDocuments: [], supplierLinks: [], poSplits: [],
  // QA
  inspections: [], exceptions: [],
  inspectionOffers: [],        // supplier inspection offer requests
  supplierSubmissions: [],     // supplier uploaded documents
  qualityProjects: [],         // DMAIC improvement projects
  successStories: [],          // completed project case studies
  qualityVision: null,         // vision, mission, goals set by admin
  commercialDecisions: [],      // CD approval records
  cdThresholds: null,           // CD trigger thresholds
};

// ─── LOGIN + REGISTRATION ───────────────────────────────────────────
//  Authentication goes through api/auth.php. Passwords are bcrypt-hashed
//  server-side and never sent to the browser. If the backend is
//  unreachable the app falls back to the local seed so the UI can still
//  be demonstrated offline.
const LoginScreen = ({ db, setDb, onLogin }) => {
  const [mode, setMode] = useState('login');       // login | register | verify
  const [user, setUser] = useState(''); const [pass, setPass] = useState('');
  const [error, setError] = useState(''); const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [reg, setReg] = useState({ username:'', name:'', email:'', phone:'', password:'', confirm:'' });
  const [otp, setOtp] = useState('');

  const handleLogin = async () => {
    setError(''); setInfo(''); setBusy(true);
    // Auto-retry up to 3 times with short delays — handles shared host transient 500s
    let lastErr = '';
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        if (attempt > 1) {
          setInfo(`Server busy — retrying (${attempt}/3)…`);
          await new Promise(r => setTimeout(r, attempt * 800));
        }
        const res = await apiPost('auth.php', { action:'login', username:user, password:pass });
        if (res.success) { setInfo(''); onLogin(res.user); setBusy(false); return; }
        // Auth failure (wrong password etc.) — don't retry
        lastErr = res.error || 'Invalid credentials';
        break;
      } catch (e) {
        lastErr = e.message;
        if (attempt === 3) {
          // All retries failed — try offline fallback
          const u = (db.users||[]).find(x => x.username===user && x.password===pass);
          if (u) { onLogin({ ...u, offline:true }); setBusy(false); return; }
        }
      }
    }
    setInfo('');
    setError(lastErr.includes('credentials') || lastErr.includes('password')
      ? lastErr
      : 'Server is busy right now — please wait a moment and try again.');
    setBusy(false);
  };

  const handleRegister = async () => {
    setError(''); setInfo('');
    if (!reg.username || !reg.email || !reg.password || !reg.name) return setError('All fields are required');
    if (reg.password !== reg.confirm) return setError('Passwords do not match');
    if (reg.password.length < 8) return setError('Password must be at least 8 characters');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(reg.email)) return setError('Enter a valid email');
    setBusy(true);
    try {
      const res = await apiPost('auth.php', { action:'register', ...reg });
      if (!res.success) { setError(res.error || 'Registration failed'); return; }
      setInfo(res.message || `Verification code sent to ${reg.email}`);
      setMode('verify');
    } catch (e) {
      setError('Cannot reach the server. Ask an administrator to create your account directly.');
    } finally { setBusy(false); }
  };

  const handleVerify = async () => {
    setError(''); setBusy(true);
    try {
      const res = await apiPost('auth.php', { action:'verify_otp', username:reg.username, otp });
      if (!res.success) { setError(res.error || 'Incorrect code'); return; }
      setInfo('Email verified. Your account is now awaiting manager approval.');
      setMode('login');
    } catch (e) { setError('Cannot reach the server.'); }
    finally { setBusy(false); }
  };

  const resendOtp = async () => {
    try {
      const res = await apiPost('auth.php', { action:'resend_otp', username:reg.username });
      setInfo(res.success ? 'New code sent.' : (res.error || 'Could not resend'));
    } catch { setError('Cannot reach the server.'); }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-logo">
          <div className="login-logo-mark">TQ</div>
          <div>
            <div className="login-logo-text">TOQUM</div>
            <span className="login-logo-sub">Quality Management System</span>
          </div>
        </div>
        {error && <div className="login-error">{error}</div>}
        {info  && <div className="login-info">{info}</div>}

        {mode==='login' && <>
          <input className="login-input" placeholder="Username" value={user}
            onChange={e=>setUser(e.target.value)} autoComplete="username" />
          <input className="login-input" placeholder="Password" type="password" value={pass}
            onChange={e=>setPass(e.target.value)} autoComplete="current-password"
            onKeyDown={e=>e.key==='Enter'&&handleLogin()} />
          <button className="login-btn" onClick={handleLogin} disabled={busy}>
            {busy ? 'SIGNING IN…' : 'LOGIN'}</button>
          <div style={{ textAlign:'center', marginTop:12, fontSize:12 }}>
            New user? <span onClick={()=>{setMode('register');setError('');setInfo('');}}
              style={{ color:'#0073ea', cursor:'pointer', fontWeight:700 }}>Register</span>
          </div>
        </>}

        {mode==='register' && <>
          <input className="login-input" placeholder="Full Name" value={reg.name} onChange={e=>setReg({...reg,name:e.target.value})} />
          <input className="login-input" placeholder="User ID (username)" value={reg.username} onChange={e=>setReg({...reg,username:e.target.value})} />
          <input className="login-input" placeholder="Email ID" value={reg.email} onChange={e=>setReg({...reg,email:e.target.value})} />
          <input className="login-input" placeholder="Phone (optional)" value={reg.phone} onChange={e=>setReg({...reg,phone:e.target.value})} />
          <input className="login-input" placeholder="Password (min 8 chars)" type="password" value={reg.password} onChange={e=>setReg({...reg,password:e.target.value})} />
          <input className="login-input" placeholder="Confirm Password" type="password" value={reg.confirm} onChange={e=>setReg({...reg,confirm:e.target.value})} />
          <button className="login-btn" onClick={handleRegister} disabled={busy}>
            {busy ? 'SUBMITTING…' : 'VERIFY EMAIL & REGISTER'}</button>
          <div style={{ textAlign:'center', marginTop:12, fontSize:12 }}>
            <span onClick={()=>{setMode('login');setError('');}}
              style={{ color:'#0073ea', cursor:'pointer', fontWeight:700 }}>Back to login</span>
          </div>
        </>}

        {mode==='verify' && <>
          <input className="login-input" placeholder="Enter the 6-digit code sent to your email"
            value={otp} onChange={e=>setOtp(e.target.value)} />
          <button className="login-btn" onClick={handleVerify} disabled={busy}>CONFIRM CODE</button>
          <div style={{ textAlign:'center', marginTop:12, fontSize:12 }}>
            <span onClick={resendOtp} style={{ color:'#0073ea', cursor:'pointer', fontWeight:700 }}>Resend code</span>
            {'  ·  '}
            <span onClick={()=>setMode('login')} style={{ color:'#0073ea', cursor:'pointer', fontWeight:700 }}>Back to login</span>
          </div>
        </>}
      </div>
    </div>
  );
};

// ─── PASSWORD CHANGE MODAL ──────────────────────────────────────────
const PasswordModal = ({ currentUser, db, setDb, saveToDB, onClose }) => {
  const [f, setF] = useState({ old:'', neu:'', confirm:'' });
  const [err, setErr] = useState('');
  const submit = async () => {
    if (f.neu.length < 8)      return setErr('New password must be at least 8 characters');
    if (f.neu !== f.confirm)   return setErr('New passwords do not match');
    try {
      const res = await apiPost('auth.php', { action:'change_password',
        username: currentUser.username, oldPassword: f.old, newPassword: f.neu });
      if (!res.success) return setErr(res.error || 'Could not change password');
      alert('Password changed. Use it at your next login.'); onClose();
    } catch (e) {
      setErr('Cannot reach the server — password unchanged.');
    }
  };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div style={{ background:'white', borderRadius:10, padding:24, width:340 }}>
        <h3 style={{ marginTop:0 }}>Change Password</h3>
        {err && <div style={{ color:T.danger, fontSize:12, marginBottom:8 }}>{err}</div>}
        {['old','neu','confirm'].map((k,i)=>(
          <input key={k} type="password" placeholder={['Current password','New password','Confirm new password'][i]}
            value={f[k]} onChange={e=>setF({...f,[k]:e.target.value})}
            style={{ width:'100%', padding:9, marginBottom:8, border:'1px solid #cbd5e1', borderRadius:5, boxSizing:'border-box' }} />
        ))}
        <div style={{ display:'flex', gap:8, marginTop:6 }}>
          <button onClick={submit} style={{ flex:1, padding:9, background:T.primary, color:'white', border:'none', borderRadius:5, fontWeight:700, cursor:'pointer' }}>Update</button>
          <button onClick={onClose} style={{ padding:'9px 14px', background:'#cbd5e1', border:'none', borderRadius:5, fontWeight:700, cursor:'pointer' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN APP ───────────────────────────────────────────────────────
export default function App() {
  const [isLoggedIn, setIsLoggedIn]   = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeModule, setActiveModule] = useState(
    currentUser?.role === 'Supplier' ? 'SUPPLIER_HOME' : 'HOME'
  );
  const [showVision, setShowVision]         = useState(false);
  const [showLogoutVision, setShowLogoutVision] = useState(false);
  const [showPwd, setShowPwd]               = useState(false);
  const [db, setDb]                         = useState(EMPTY_DB);
  const [dbLoading, setDbLoading]           = useState(false);

  // Show vision reminder 2s after db loads on first login of the day
  useEffect(() => {
    if (!db.qualityVision?.showOnLogin) return;
    if (!db.qualityVision?.vision && !db.qualityVision?.goals?.some(g=>g.text)) return;
    const key = `tq_vision_${currentUser?.username}_${new Date().toDateString()}`;
    if (sessionStorage.getItem(key)) return;
    const t = setTimeout(() => setShowVision(true), 2000);
    return () => clearTimeout(t);
  }, [db.qualityVision]); // eslint-disable-line

  // Load data AFTER login — not on page load (avoids competing with auth request on shared hosting)
  const loadDb = () => {
    setDbLoading(true);
    fetch(`${API_URL}/get_all_data.php`, {
      headers: { 'Accept': 'application/json', 'Cache-Control': 'no-cache' }
    })
      .then(r => r.text())
      .then(t => {
        const clean = t.trim().replace(/^\uFEFF/, '').replace(/^[^{[]*/, '');
        const data = JSON.parse(clean);
        if (data) setDb(prev => ({ ...prev, ...data }));
      })
      .catch(err => console.warn('Toqum running with local data:', err.message))
      .finally(() => setDbLoading(false));
  };

  const [saveError, setSaveError] = useState(null);

  const saveToDB = async (category, data) => {
    try {
      const res = await fetch(`${API_URL}/save_universal.php`, {
        method:'POST', headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ category, data }),
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Save failed');
      return result;
    } catch (e) {
      console.error('saveToDB failed:', category, e.message);
      setSaveError(`⚠ DB save failed (${category}): ${e.message}`);
      setTimeout(() => setSaveError(null), 6000);
      return null;
    }
  };

  if (!isLoggedIn) return <LoginScreen db={db} setDb={setDb}
    onLogin={u => { setIsLoggedIn(true); setCurrentUser(u); loadDb(); }} />;

  const isSupplierRole = currentUser?.role === 'Supplier';

  const ALWAYS_VISIBLE = new Set(['HOME','DASHBOARD','QA','CD','TRAINING','PROJECTS','FEEDBACK']);

  const visibleModules = isSupplierRole
    ? { SUPPLIER_HOME: { label:'MY PORTAL', icon:'🏭' }, TRAINING: { label:'TRAINING', icon:'🎓' } }
    : Object.fromEntries(Object.entries(MODULES).filter(([m]) =>
        ALWAYS_VISIBLE.has(m) || hasRight(currentUser, m, 'view')));

  const common = { db, setDb, saveToDB, currentUser };
  const renderModule = () => {
    // Supplier sees a simplified portal — just their own data
    if (isSupplierRole) {
      if (activeModule === 'TRAINING') return <Training {...common} />;
      return <SupplierPortalHome {...common} />;
    }
    switch (activeModule) {
      case 'HOME':     return <LandingPage db={db} currentUser={currentUser} setActiveModule={setActiveModule} saveToDB={saveToDB} />;
      case 'ADMIN':    return <AdminModule {...common} />;
      case 'TEAM':     return <TeamModule {...common} />;
      case 'SUPPLIER': return <SupplierModule {...common} />;
      case 'STYLE':    return <StyleModule {...common} />;
      case 'SAMPLE':   return <SampleEvaluation {...common} />;
      case 'QA':       return <QAModule {...common} />;
      case 'CD':       return <CommercialDecisions {...common} />;
      case 'PROJECTS': return <ProjectModule {...common} />;
      case 'FEEDBACK': return <CustomerFeedback {...common} />;
      case 'TRAINING': return <Training {...common} />;
      default:         return <QualityDashboard {...common} />;
    }
  };

  return (
    <ErrorBoundary>
      <div className="app-shell">
        {dbLoading && (
          <div style={{ position:'fixed', top:0, left:0, right:0, zIndex:9998,
            background:'linear-gradient(90deg,#0073ea,#00c875)',
            height:3, animation:'dbLoadBar 2s ease-in-out infinite' }}>
            <style>{`@keyframes dbLoadBar{0%{opacity:1}50%{opacity:.5}100%{opacity:1}}`}</style>
          </div>
        )}
        <header className="nav-tier-1">
          <div className="brand-zone" onClick={()=>setActiveModule(isSupplierRole?'SUPPLIER_HOME':'HOME')}>
            <div className="brand-logo">TQ</div>
            <div>
              <div className="brand-name">TOQUM</div>
              <span className="brand-tagline">Quality System</span>
            </div>
          </div>
          <nav className="nav-1-links">
            {Object.entries(visibleModules).map(([key, mod]) => (
              <button key={key} className={`nav-btn-1 ${activeModule===key?'active':''}`}
                onClick={()=>setActiveModule(key)}>
                <span>{mod.icon}</span> {mod.label}
              </button>
            ))}
          </nav>
          <div className="user-zone">
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:'11.5px', fontWeight:600, color:'white' }}>{currentUser?.name}</div>
              <div style={{ fontSize:'9px', color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.08em', cursor:'pointer' }}
                onClick={()=>setShowPwd(true)}>{currentUser?.role} · 🔑</div>
            </div>
            <div className="user-avatar">
              {(currentUser?.name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
            </div>
            <button className="logout-btn" onClick={()=> {
              const v = db.qualityVision;
              if (v?.showOnLogout && (v?.vision||v?.goals?.some(g=>g.text))) {
                setShowLogoutVision(true);
              } else {
                setIsLoggedIn(false); setCurrentUser(null);
              }
            }}>Logout</button>
          </div>
        </header>
        <main className="view-stage" style={{ flex:1, overflow:'auto', minHeight:0, display:'block', background:T.bg }}>
          {renderModule()}
        </main>
        {showPwd && <PasswordModal currentUser={currentUser} db={db} setDb={setDb} saveToDB={saveToDB} onClose={()=>setShowPwd(false)} />}
        <Andon db={db} currentUser={currentUser} currentModule={activeModule} />

        {/* DB save error toast */}
        {saveError && (
          <div style={{ position:'fixed', bottom:24, left:'50%', transform:'translateX(-50%)',
            zIndex:9999, background:'#b4232f', color:'white',
            padding:'12px 20px', borderRadius:10, fontSize:13, fontWeight:600,
            boxShadow:'0 4px 20px rgba(0,0,0,0.3)', maxWidth:500, textAlign:'center',
            animation:'tqToastIn .25s ease-out' }}>
            {saveError}
            <button onClick={()=>setSaveError(null)}
              style={{ marginLeft:12, background:'rgba(255,255,255,0.2)',
                border:'none', color:'white', borderRadius:4, padding:'2px 8px',
                cursor:'pointer', fontSize:12 }}>✕</button>
          </div>
        )}

        {/* Quality Vision — first login of day */}
        {showVision && db.qualityVision?.showOnLogin &&
         (db.qualityVision?.vision || db.qualityVision?.goals?.some(g=>g.text)) && (
          <div style={{ position:'fixed', inset:0, zIndex:5000,
            background:'rgba(10,14,26,0.82)', display:'flex', alignItems:'center',
            justifyContent:'center', padding:20, backdropFilter:'blur(8px)' }}>
            <div style={{ maxWidth:700, width:'100%',
              animation:'tqSlideUp .35s cubic-bezier(.34,1.56,.64,1)' }}>
              <QualityVisionCard vision={db.qualityVision} onClose={()=>{
                sessionStorage.setItem(`tq_vision_${currentUser?.username}_${new Date().toDateString()}`,'1');
                setShowVision(false);
              }}/>
            </div>
          </div>
        )}

        {/* Quality Vision — day closing before logout */}
        {showLogoutVision && (
          <div style={{ position:'fixed', inset:0, zIndex:5000,
            background:'rgba(10,14,26,0.85)', display:'flex', alignItems:'center',
            justifyContent:'center', padding:20, backdropFilter:'blur(8px)' }}>
            <div style={{ maxWidth:700, width:'100%' }}>
              <div style={{ textAlign:'center', marginBottom:14, color:'rgba(255,255,255,0.7)',
                fontSize:12, fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase' }}>
                Day Closing · Reflect Before You Go
              </div>
              <QualityVisionCard vision={db.qualityVision}/>
              <div style={{ marginTop:14, display:'flex', gap:10, justifyContent:'center' }}>
                <button onClick={()=>setShowLogoutVision(false)}
                  style={{ padding:'10px 24px', background:'rgba(255,255,255,0.08)',
                    border:'1px solid rgba(255,255,255,0.2)', color:'white', borderRadius:8,
                    fontSize:13, fontWeight:700, cursor:'pointer' }}>
                  ← Stay & Continue
                </button>
                <button onClick={()=>{ setIsLoggedIn(false); setCurrentUser(null); }}
                  style={{ padding:'10px 24px',
                    background:'linear-gradient(135deg,#e2445c,#c43050)',
                    border:'none', color:'white', borderRadius:8,
                    fontSize:13, fontWeight:700, cursor:'pointer',
                    boxShadow:'0 2px 12px rgba(226,68,92,0.4)' }}>
                  End Day & Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}
